import { EventFormat } from '.'
import { Address } from './Address'
import { Gender, Response, Role, Status } from './Common'
import { FlattenType } from './typeHelper'

export type ProvisionalRatings = {
  singlesRating: number
  doublesRating: number
  coach?: { id?: string }
}

export type Stats = {
  singles: string
  singlesVerified: string
  singlesProvisional: boolean
  singlesReliabilityScore: string
  doublesReliabilityScore: string
  doubles: string
  doublesVerified: string
  doublesProvisional: boolean
  defaultRating: string
  provisionalRatings: ProvisionalRatings
}

type SubscriptionState = 'SUBSCRIBED' | 'UNSUBSCRIBED'

export type UserPreferences = {
  enableSms?: boolean
  enableEmail?: boolean
  enablePush?: boolean
  enablePrivacy?: boolean
  enableNewsletter?: boolean
  hideAge?: boolean
  hideLocation?: boolean
  adsDoNotTrack?: boolean
  consentToTargetedAds?: boolean
  newsletter?: SubscriptionState
  partnerEmails?: SubscriptionState
  minorLeaguePickleball?: SubscriptionState
  leaguesAndTours?: SubscriptionState
  coaching?: SubscriptionState
  lastTosAcceptTime?: string
}

export function normalizeUserPreferences(
  apiResponse: Partial<Record<keyof UserPreferences, string | boolean | undefined>>
): UserPreferences {
  return Object.fromEntries(
    Object.entries(apiResponse).map(([key, value]) => [
      key,
      value === 'true' ? true : value === 'false' ? false : value,
    ])
  ) as UserPreferences
}

export type User = {
  active: true
  addresses: Address[]
  birthdate: string
  displayUsername: boolean
  email: string
  fullName: string
  gender: Gender
  id: number
  imageUrl: string
  isValidEmail: boolean
  isValidPhone: boolean
  isoCode: string
  phone: string
  preferredSide?: string
  referralCode: string
  role: Role
  stats: Stats
  userPreferences: UserPreferences
  hand?: any
}

export type GetProfileResponse = {
  status: Status
  result: User
}

export type Average = {
  averageOpponentDupr: string
  averagePartnerDupr: string
  averagePointsWonPercent: string
  halfLife: string
  losses: number
  wins: number
}

export type ResultOverview = {
  losses: number
  pending: number
  wins: number
}

export type StatsInfo = {
  doubles: Average
  resultOverview: ResultOverview
  singles: Average
}

export enum Hand {
  LEFT = 'LEFT',
  RIGHT = 'RIGHT',
}

export type GetUserStatsResponse = FlattenType<Response & { result: StatsInfo }>

export type PayloadUpdateProfile = {
  addressId?: number
  apparelBrand?: string
  birthdate?: string
  defaultRating?: EventFormat | 'NONE'
  displayUsername?: string
  fullName: string
  gender?: Gender
  hand?: Hand
  isValidPhone?: boolean
  isoCode?: string
  mediaId?: number
  paddleBrand?: string
  phone?: string
  placeId?: string
  preferredBall?: string
  preferredSide?: 'LEFT' | 'RIGHT' | 'EITHER'
  shoeBrand?: string
  username?: string
  // User-backed settings (privacy/advertising/communication toggles) are now managed
  // entirely on the schema-driven preferences page, so the profile save no longer sends
  // them. Kept optional for any remaining callers that still piggy-back settings here.
  userPreferences?: { settings: UserPreferences }
}

export type PayloadUpdateUserSettings = { settings: UserPreferences }

export type PayloadVerifyPhone = {
  isoCode: string
  otp: string
  phoneNumber: string
}

export type PayloadResendVerifyPhone = {
  phoneNumber: string
}

export type PayloadResetPassword = {
  newPassword: string
  oldPassword: string
}

export type GetUserInitializationResponseResult = {
  status: string
  data: null | {
    daysLeftForInitialization: number
    eventFormat: EventFormat
    initializationStatus: string
    playerId: string
    playerName: string
    qualificationScore: number
    minutesLeftForInitialization: number
  }
  message: string
}

export type GetUserInitializationResponse = FlattenType<Response & { results: GetUserInitializationResponseResult[] }>
